import { createContext, useEffect, useCallback, useContext, useMemo, useState } from "react";
import { SessionTokens } from "./types";
import { clearListMemory } from "./listStateStorage";

const SESSION_KEY = "brandwise.web.session";

type SessionContextValue = {
  accessToken: string | null;
  refreshToken: string | null;
  saveSession: (tokens: SessionTokens) => void;
  clearSession: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

function readStoredSession(): SessionTokens | null {
  try {
    const value = window.localStorage.getItem(SESSION_KEY);
    return value ? (JSON.parse(value) as SessionTokens) : null;
  } catch {
    return null;
  }
}

export function getStoredSession() {
  return readStoredSession();
}

export function storeSession(tokens: SessionTokens) {
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(tokens));
}

export function removeStoredSession() {
  clearListMemory();
  window.localStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new Event("coprint:session-cleared"));
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionTokens | null>(() => readStoredSession());

  useEffect(() => {
    const clear = () => setSession(null);
    window.addEventListener("coprint:session-cleared", clear);
    return () => window.removeEventListener("coprint:session-cleared", clear);
  }, []);

  const saveSession = useCallback((tokens: SessionTokens) => {
    storeSession(tokens);
    setSession(tokens);
  }, []);

  const clearSession = useCallback(() => {
    removeStoredSession();
    setSession(null);
  }, []);

  const value = useMemo<SessionContextValue>(() => ({
    accessToken: session?.accessToken ?? null,
    refreshToken: session?.refreshToken ?? null,
    saveSession,
    clearSession
  }), [clearSession, saveSession, session]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession must be used inside SessionProvider.");
  }
  return value;
}
